import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { storiesAPI } from "@/lib/api";
import { Award, Send } from "lucide-react";
import { toast } from "sonner";

const PROFESSIONS = [
  "Doctor", "Nurse", "Physiotherapist", "Occupational Therapist",
  "Speech & Language Therapist", "Radiographer", "Pharmacist", "Other"
];
const BANDS = ["Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b"];

export default function SuccessStoryDialog({ open, onOpenChange }) {
  const [profession, setProfession] = useState("");
  const [bandAchieved, setBandAchieved] = useState("");
  const [testimonial, setTestimonial] = useState("");
  const [anonymous, setAnonymous] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!profession || !bandAchieved || !testimonial.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await storiesAPI.submit({
        profession,
        band_achieved: bandAchieved,
        testimonial: testimonial.trim(),
        anonymous,
      });
      toast.success("Thank you! Your success story has been submitted.");
      onOpenChange(false);
      setProfession("");
      setBandAchieved("");
      setTestimonial("");
    } catch {
      toast.error("Failed to submit story");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="success-story-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#003087]" style={{ fontFamily: "Public Sans" }}>
            <Award className="w-5 h-5 text-[#FFB81C]" /> Share Your Success
          </DialogTitle>
          <DialogDescription>
            Did you get shortlisted or offered the role? Share your story to inspire other NHS professionals.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Profession</Label>
              <Select value={profession} onValueChange={setProfession}>
                <SelectTrigger data-testid="story-profession"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {PROFESSIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Band Achieved</Label>
              <Select value={bandAchieved} onValueChange={setBandAchieved}>
                <SelectTrigger data-testid="story-band"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {BANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Your Story</Label>
            <Textarea
              placeholder="Tell us how BandBoost AI helped you succeed..."
              className="min-h-[100px]"
              value={testimonial}
              onChange={(e) => setTestimonial(e.target.value)}
              data-testid="story-testimonial"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="story-anonymous-switch" />
              <Label className="text-sm text-slate-600">Share anonymously</Label>
            </div>
          </div>
          <Button
            className="w-full bg-[#003087] text-white hover:bg-[#002366]"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="story-submit-btn"
          >
            <Send className="w-4 h-4 mr-2" /> {submitting ? "Submitting..." : "Submit Story"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
